import React, { useState } from 'react';
import { useToast } from '../../../hooks/useToast';
import {
  useGetHolidayRequestsQuery,
  useGetUserProfilesQuery,
  useCreateHolidayRequestForUserMutation,
  useApproveHolidayRequestMutation,
  useRejectHolidayRequestMutation,
  useToggleHolidayRequestStatusMutation,
  useGetBankHolidaysQuery,
  HOLIDAY_FETCH_PAGE_SIZE,
} from '../../../store/slices/apiSlice';
import { AdminHolidayRequestForm } from '../components/AdminHolidayRequestForm';
import { AdminHolidayTable } from '../components/AdminHolidayTable';
import type { UserProfile } from '../../../utils/types';

export const AdminTab: React.FC = () => {
  const toast = useToast();
  const currentYear = new Date().getFullYear();
  const [processingIds, setProcessingIds] = useState<Record<string, boolean>>({});

  // Fetch all holiday requests
  const {
    data: requestsData,
    isLoading: isRequestsLoading,
    refetch: refetchRequests,
  } = useGetHolidayRequestsQuery({ year: currentYear, page_size: HOLIDAY_FETCH_PAGE_SIZE });

  // Fetch users for dropdown
  const {
    data: usersData,
    isLoading: isUsersLoading,
  } = useGetUserProfilesQuery({ page_size: 1000 });

  // Fetch bank holidays
  const {
    data: bankHolidays,
  } = useGetBankHolidaysQuery();

  // Mutations
  const [createRequest, { isLoading: isCreating }] = useCreateHolidayRequestForUserMutation();
  const [approveRequest, { isLoading: isApproving }] = useApproveHolidayRequestMutation();
  const [rejectRequest, { isLoading: isRejecting }] = useRejectHolidayRequestMutation();
  const [toggleStatus, { isLoading: isToggling }] = useToggleHolidayRequestStatusMutation();

  const requests = requestsData?.results || [];
  const users = (usersData?.results || []) as UserProfile[];

  const handleCreateRequest = async (formData: {
    user_id: number;
    start_date: string;
    end_date: string;
    reason: string;
    is_half_day: boolean;
  }) => {
    try {
      await createRequest(formData).unwrap();
      toast.success(
        'Request Created',
        'Holiday request has been created successfully.'
      );
      refetchRequests();
    } catch (error: any) {
      const errorMessage = error?.data?.error || 'Failed to create holiday request';
      toast.error('Creation Failed', errorMessage);
    }
  };

  const handleApprove = async (id: string, notes?: string) => {
    setProcessingIds((prev) => ({ ...prev, [id]: true }));
    try {
      await approveRequest({ id, notes }).unwrap();
      toast.success('Request Approved', 'The holiday request has been approved.');
      refetchRequests();
    } catch (error: any) {
      const errorMessage = error?.data?.error || 'Failed to approve request';
      toast.error('Approval Failed', errorMessage);
    } finally {
      setProcessingIds((prev) => ({ ...prev, [id]: false }));
    }
  };

  const handleReject = async (id: string, reason: string) => {
    setProcessingIds((prev) => ({ ...prev, [id]: true }));
    try {
      await rejectRequest({ id, reason }).unwrap();
      toast.success('Request Rejected', 'The holiday request has been rejected.');
      refetchRequests();
    } catch (error: any) {
      const errorMessage = error?.data?.error || 'Failed to reject request';
      toast.error('Rejection Failed', errorMessage);
    } finally {
      setProcessingIds((prev) => ({ ...prev, [id]: false }));
    }
  };

  const handleToggleStatus = async (id: string, reason?: string) => {
    setProcessingIds((prev) => ({ ...prev, [id]: true }));
    try {
      await toggleStatus({ id, reason }).unwrap();
      toast.success('Status Changed', 'The request status has been updated.');
      refetchRequests();
    } catch (error: any) {
      const errorMessage = error?.data?.error || 'Failed to change status';
      toast.error('Status Change Failed', errorMessage);
    } finally {
      setProcessingIds((prev) => ({ ...prev, [id]: false }));
    }
  };

  // Calculate summary stats
  const pendingCount = requests.filter((r) => r.status === 'pending').length;
  const approvedCount = requests.filter((r) => r.status === 'approved').length;
  const rejectedCount = requests.filter((r) => r.status === 'rejected').length;

  return (
    <div className="space-y-6">
      {/* Summary Stats */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-amber-50 rounded-lg border border-amber-200 p-4">
          <p className="text-sm text-amber-700 font-medium">Pending Requests</p>
          <p className="text-2xl font-bold text-amber-900">{pendingCount}</p>
        </div>
        <div className="bg-green-50 rounded-lg border border-green-200 p-4">
          <p className="text-sm text-green-700 font-medium">Approved</p>
          <p className="text-2xl font-bold text-green-900">{approvedCount}</p>
        </div>
        <div className="bg-red-50 rounded-lg border border-red-200 p-4">
          <p className="text-sm text-red-700 font-medium">Rejected</p>
          <p className="text-2xl font-bold text-red-900">{rejectedCount}</p>
        </div>
      </div>

      {/* Create Request Form */}
      <AdminHolidayRequestForm
        users={users}
        onSubmit={handleCreateRequest}
        isSubmitting={isCreating}
        bankHolidays={bankHolidays ?? []}
      />

      {/* All Requests Table */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">All Holiday Requests</h3>
          <p className="text-sm text-gray-500 mt-1">
            Manage all holiday requests for {currentYear}
          </p>
        </div>
        <div className="p-6">
          <AdminHolidayTable
            requests={requests}
            isLoading={isRequestsLoading}
            onApprove={handleApprove}
            onReject={handleReject}
            onToggleStatus={handleToggleStatus}
            isProcessing={processingIds}
          />
        </div>
      </div>
    </div>
  );
};

export default AdminTab;
